"use client";

import { publicService } from "@/services/public.service";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";

function Icon({ name, className = "", fill = false }: { name: string; className?: string; fill?: boolean }) {
  return <span className={`material-symbols-outlined ${fill ? "fill" : ""} ${className}`}>{name}</span>;
}

const REQUEST_TYPES = [
  { ar: "استفسار عام", en: "General inquiry" },
  { ar: "مشكلة في الحجز", en: "Booking issue" },
  { ar: "الدفع والاسترداد", en: "Payment & refund" },
  { ar: "الحساب والأمان", en: "Account & security" },
  { ar: "أن أصبح مضيفاً", en: "Becoming a host" },
  { ar: "أخرى", en: "Other" },
];

const MAX = 500;

export default function HelpRequestForm({ isAr }: { isAr: boolean }) {
  const t = (a: string, e: string) => (isAr ? a : e);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [type, setType] = useState(REQUEST_TYPES[0].en);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [fileName, setFileName] = useState("");
  const [agree, setAgree] = useState(false);

  const mutation = useMutation({
    mutationFn: () => {
      const typeLabel = REQUEST_TYPES.find((r) => r.en === type);
      const fullSubject = [typeLabel ? t(typeLabel.ar, typeLabel.en) : type, subject].filter(Boolean).join(" — ");
      return publicService.sendContactMessage({ name, email, phone, subject: fullSubject, message });
    },
    onSuccess: () => {
      toast.success(t("تم إرسال طلبك بنجاح! سنرد عليك قريباً.", "Your request was sent! We'll get back to you soon."));
      setName(""); setEmail(""); setPhone(""); setSubject(""); setMessage(""); setFileName(""); setAgree(false);
      setType(REQUEST_TYPES[0].en);
    },
    onError: () => toast.error(t("تعذّر إرسال الطلب، حاول مجدداً.", "Couldn't send your request, please try again.")),
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return toast.error(t("الاسم مطلوب", "Name is required"));
    if (!message.trim()) return toast.error(t("الرسالة مطلوبة", "Message is required"));
    if (!agree) return toast.error(t("يرجى الموافقة على سياسة الخصوصية", "Please accept the privacy policy"));
    mutation.mutate();
  };

  const field = "w-full rounded-xl border border-clay-100 bg-surface px-4 py-3 text-sm text-ink-900 outline-none focus:border-clay-600";
  const label = "block text-sm font-bold text-ink-900 mb-1.5";

  return (
    <form onSubmit={onSubmit} className="rounded-3xl border border-clay-100 bg-surface p-6 md:p-8">
      <h3 className="font-display text-xl font-bold text-ink-900 mb-6">{t("إرسال طلب مساعدة", "Send a help request")}</h3>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={label}>{t("الاسم الكامل", "Full name")}</label>
          <input value={name} onChange={(e) => setName(e.target.value)} className={field} placeholder={t("اسمك", "Your name")} />
        </div>
        <div>
          <label className={label}>{t("البريد الإلكتروني", "Email")}</label>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={field} placeholder="name@email.com" dir="ltr" />
        </div>
        <div>
          <label className={label}>{t("رقم الجوال", "Phone number")}</label>
          <input value={phone} onChange={(e) => setPhone(e.target.value)} className={field} placeholder="05xxxxxxxx" dir="ltr" />
        </div>
        <div>
          <label className={label}>{t("نوع الطلب", "Request type")}</label>
          <select value={type} onChange={(e) => setType(e.target.value)} className={field}>
            {REQUEST_TYPES.map((r) => (
              <option key={r.en} value={r.en}>{t(r.ar, r.en)}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="mt-4">
        <label className={label}>{t("الموضوع", "Subject")}</label>
        <input value={subject} onChange={(e) => setSubject(e.target.value)} className={field} placeholder={t("عنوان مختصر لطلبك", "A short title for your request")} />
      </div>

      <div className="mt-4">
        <label className={label}>{t("رسالتك", "Your message")}</label>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value.slice(0, MAX))}
          rows={4}
          className={`${field} resize-none`}
          placeholder={t("اكتب تفاصيل طلبك هنا…", "Write the details of your request here…")}
        />
        <div className="text-end text-xs text-ink-500 mt-1">{message.length} / {MAX}</div>
      </div>

      {/* Attachment (optional) */}
      <div className="mt-4">
        <label className={label}>{t("إرفاق ملفات (اختياري)", "Attach files (optional)")}</label>
        <label className="flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-clay-100 bg-clay-50/50 p-6 text-center cursor-pointer hover:border-clay-300 transition-colors">
          <Icon name="cloud_upload" className="text-clay-600 text-3xl" />
          <span className="text-sm text-ink-500">
            {fileName || t("اسحب الملفات هنا أو اضغط للتصفّح", "Drag files here or click to browse")}
          </span>
          <input type="file" className="hidden" onChange={(e) => setFileName(e.target.files?.[0]?.name || "")} />
        </label>
      </div>

      {/* Consent */}
      <label className="mt-5 flex items-start gap-2 cursor-pointer">
        <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-1 accent-clay-600" />
        <span className="text-sm text-ink-500">
          {t("أوافق على سياسة الخصوصية واستخدام بياناتي لغرض معالجة الطلب.", "I agree to the privacy policy and the use of my data to process this request.")}
        </span>
      </label>

      <button
        type="submit"
        disabled={mutation.isPending}
        className="mt-6 w-full rounded-full bg-clay-600 text-white py-3.5 font-bold hover:bg-clay-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
      >
        <Icon name="send" className="text-[20px]" />
        {mutation.isPending ? t("جارٍ الإرسال…", "Sending…") : t("إرسال الطلب", "Send request")}
      </button>
    </form>
  );
}
